import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Warning, CaretDown, CaretRight, Phone, Globe, EnvelopeSimple, User, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AccessDenied } from "@/components/AccessDenied";
import { formatLocal } from "@/lib/timezone";

const TABS = [
  { k: "pending", label: "Pending", color: "text-[#DC2626]" },
  { k: "approved", label: "Approved", color: "text-[#16A34A]" },
  { k: "rejected", label: "Rejected", color: "text-[#525252]" },
];

function scoreBadge(score, reasons) {
  if ((reasons || "").includes("[PULSE-TEST]")) return "bg-[#E0E7FF] text-[#3730A3]";
  if (score >= 6) return "bg-[#FEE2E2] text-[#991B1B]";
  if (score >= 3) return "bg-[#FEF3C7] text-[#92400E]";
  return "bg-[#F0FDF4] text-[#166534]";
}
function scoreLabel(score, reasons) {
  if ((reasons || "").includes("[PULSE-TEST]")) return "TEST";
  return `Score ${score}`;
}

/** Map raw scorer reason strings (some contain regex patterns) into short,
 *  human-readable labels suitable for review-screen pills. */
function humanizeReason(raw) {
  const s = (raw || "").toLowerCase();
  if (s.includes("disposable")) return "Disposable email domain";
  if (s.includes("personal email")) return "Personal email domain";
  if (s.includes("random")) return "Random-looking email handle";
  if (s.includes("name looks fake")) return "Suspicious name";
  if (s.includes("company is a test")) return "Company is a test value";
  if (s.includes("empty or too-short company")) return "Company too short / blank";
  if (s.includes("company contains a url")) return "Company name is a URL";
  if (s.includes("name contains a url")) return "Name contains a URL";
  if (s.includes("phone has only")) return "Phone too short";
  if (s.includes("contains url")) return "Message contains URLs";
  if (s.includes("contribut")) return "Guest-post / content-contribution pitch";
  if (s.includes("guest")) return "Guest-post outreach";
  if (s.includes("write")) return "Offer to write content";
  if (s.includes("backlink")) return "Backlink / link-building offer";
  if (s.includes("sponsored")) return "Sponsored-content pitch";
  if (s.includes("punchout")) return "Competitor PunchOut self-mention";
  if (s.includes("promote")) return "Self-promotion language";
  if (s.includes("our product") || s.includes("our service") || s.includes("our solution") ||
      s.includes("our catalog") || s.includes("our platform") || s.includes("our tool"))
    return "Self-promotion language";
  if (s.includes("introduce") || s.includes("introducing")) return "Cold pitch introduction";
  if (s.includes("showcase") || s.includes("highlight")) return "Self-showcase pitch";
  if (s.includes("specializes") || s.includes("specialises") || s.includes("focuses"))
    return "Vendor pitch ('we specialize in…')";
  if (s.includes("streamline")) return "Generic procurement-pitch jargon";
  if (s.includes("name contains '@'")) return "Name looks like an email";
  if (s.includes("abnormally long")) return "Message abnormally long";
  // Fall back: drop the regex content, keep the prefix.
  if (s.startsWith("message matches spam pattern")) return "Spam keyword pattern";
  return raw;
}

/** Strip the auto-appended "How did you find us / Source" footer from the
 *  message body so we don't duplicate the dedicated fields below. */
function cleanMessage(message, foundUs, sourcePage) {
  if (!message) return "";
  let m = message;
  // Remove the boilerplate footer the public-leads endpoint appends.
  m = m.replace(/\s*-+\s*\nHow did you find us:[^\n]*(\n|$)/gi, "\n").trim();
  m = m.replace(/Source:\s*https?:\/\/\S+/gi, "").trim();
  // If we just have the original prospect text now, return it.
  return m;
}

export default function PendingLeadsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);
  const [showTech, setShowTech] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/pending-leads?status=${tab}`);
      setRows(r.data || []);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      else toast.error("Failed to load pending leads");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Reset transient editor state when switching selection.
  useEffect(() => { setRejectReason(""); setShowTech(false); }, [selected?.id]);

  const approve = async (id) => {
    if (!window.confirm("Approve this lead and add to the CRM?")) return;
    setActing(true);
    try {
      await api.post(`/crm/pending-leads/${id}/approve`, {});
      toast.success("Lead approved and added to CRM");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    setActing(false);
  };

  const reject = async (id) => {
    setActing(true);
    try {
      await api.post(`/crm/pending-leads/${id}/reject`, { reason: rejectReason });
      toast.success("Lead rejected");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    setActing(false);
  };

  if (denied) return <AccessDenied module="Sales Leads" />;

  return (
    <div className="p-6" data-testid="pending-leads-page">
      <button onClick={() => navigate("/crm/leads")} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] mb-4" data-testid="back-to-leads">
        <ArrowLeft size={14} /> Back to Sales Leads
      </button>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Warning size={20} className="text-[#DC2626]" weight="fill" />
        <h1 className="text-xl font-black text-[#0A0A0A] tracking-tight">Pending Lead Review</h1>
        <p className="text-xs text-[#A3A3A3]">— Approve legitimate ones; reject junk.</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E5E5] mb-3">
        {TABS.map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${tab === t.k ? `${t.color} border-current` : "border-transparent text-[#A3A3A3] hover:text-[#525252]"}`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#A3A3A3]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-10 text-center">
          <p className="text-sm text-[#525252]">No {tab} leads.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-0 border border-[#E5E5E5]">
          {/* List */}
          <div className="border-r border-[#E5E5E5] max-h-[78vh] overflow-y-auto">
            {rows.map(r => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className={`w-full text-left px-4 py-2.5 border-b border-[#F5F5F5] hover:bg-[#FAFAFA] ${selected?.id === r.id ? "bg-[#EFF6FF]" : "bg-white"}`}
                data-testid={`pending-row-${r.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{r.company_name || "(no company)"}</p>
                  <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${scoreBadge(r.suspicion_score, r.suspicion_reasons)}`}>
                    {scoreLabel(r.suspicion_score, r.suspicion_reasons)}
                  </span>
                </div>
                <p className="text-[11px] text-[#525252] truncate">{r.full_name || "—"} · <span className="text-[#A3A3A3]">{r.contact_email}</span></p>
                <p className="text-[10px] text-[#A3A3A3] mt-0.5">{r.created_at ? formatLocal(r.created_at) : ""}</p>
              </button>
            ))}
          </div>

          {/* Detail — compact */}
          <div className="bg-white max-h-[78vh] overflow-y-auto">
            {!selected ? (
              <p className="text-sm text-[#A3A3A3] p-6">Select a lead on the left.</p>
            ) : (
              <DetailPanel
                lead={selected}
                rejectReason={rejectReason}
                setRejectReason={setRejectReason}
                acting={acting}
                onApprove={() => approve(selected.id)}
                onReject={() => reject(selected.id)}
                onView={(id) => navigate(`/crm/leads/${id}`)}
                showTech={showTech}
                setShowTech={setShowTech}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ lead, rejectReason, setRejectReason, acting, onApprove, onReject, onView, showTech, setShowTech }) {
  const reasons = (lead.suspicion_reasons || "").split(" | ").filter(Boolean);
  const humanReasons = Array.from(new Set(reasons.map(humanizeReason)));
  const cleanedMessage = cleanMessage(lead.message, lead.how_did_you_find_us, lead.source_page);
  const hasFinding = lead.how_did_you_find_us || lead.source_page;

  return (
    <div data-testid="pending-detail">
      {/* Sticky header — name + score + ID + action buttons all in one strip */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5E5] px-5 py-3 flex items-center gap-3 flex-wrap">
        <h2 className="text-base font-black tracking-tight text-[#0A0A0A] truncate">{lead.company_name || "(no company)"}</h2>
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${scoreBadge(lead.suspicion_score, lead.suspicion_reasons)}`}>
          {scoreLabel(lead.suspicion_score, lead.suspicion_reasons)}
        </span>
        <span className="text-[10px] font-mono text-[#A3A3A3] truncate">{lead.id}</span>

        {lead.review_status === "pending" && (
          <div className="flex items-center gap-1.5 ml-auto">
            <button onClick={onApprove} disabled={acting}
                    className="inline-flex items-center gap-1 px-3 h-8 bg-[#16A34A] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#15803D] disabled:opacity-40"
                    data-testid="approve-button">
              <Check size={12} weight="bold" /> Approve
            </button>
            <button onClick={onReject} disabled={acting}
                    className="inline-flex items-center gap-1 px-3 h-8 bg-[#DC2626] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#991B1B] disabled:opacity-40"
                    data-testid="reject-button">
              <X size={12} weight="bold" /> Reject
            </button>
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Suspicion reasons — humanized pills, tight */}
        {humanReasons.length > 0 && (
          <div className="bg-[#FEF2F2] border-l-2 border-[#DC2626] px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#991B1B]">Why flagged</p>
              {humanReasons.map((r, i) => (
                <span key={i} className="px-2 py-0.5 text-[11px] bg-white border border-[#FECACA] text-[#991B1B] font-semibold">
                  {r}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Submitter — single inline row, no field labels */}
        <div className="flex items-center gap-x-5 gap-y-1 flex-wrap text-sm text-[#171717]">
          <span className="inline-flex items-center gap-1.5"><User size={14} className="text-[#A3A3A3]" />{lead.full_name || "—"}</span>
          <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} className="text-[#A3A3A3]" /><a href={`mailto:${lead.contact_email}`} className="hover:text-[#0055FF]">{lead.contact_email}</a></span>
          {lead.phone_number && (
            <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#A3A3A3]" /><a href={`tel:${lead.phone_number}`} className="hover:text-[#0055FF]">{lead.phone_number}</a></span>
          )}
          {lead.created_at && (
            <span className="inline-flex items-center gap-1.5 text-[#A3A3A3] text-xs"><ClockCounterClockwise size={14} />{formatLocal(lead.created_at)}</span>
          )}
        </div>

        {/* Source — compact one-liner */}
        {hasFinding && (
          <div className="text-xs text-[#525252] flex items-center gap-1.5 flex-wrap">
            <Globe size={13} className="text-[#A3A3A3]" />
            {lead.how_did_you_find_us && <><span className="text-[#A3A3A3]">via</span> <strong className="text-[#171717]">{lead.how_did_you_find_us}</strong></>}
            {lead.source_page && <><span className="text-[#A3A3A3]">·</span> <a href={lead.source_page} target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline truncate max-w-md" title={lead.source_page}>{lead.source_page.replace(/^https?:\/\//, "")}</a></>}
            {lead.utm_source && <><span className="text-[#A3A3A3]">·</span> <span>utm: {lead.utm_source}</span></>}
          </div>
        )}

        {/* Message — the actual content, full width, prominent */}
        {cleanedMessage && (
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 text-[14px] text-[#171717] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "Georgia, serif" }} data-testid="pending-message">
            {cleanedMessage}
          </div>
        )}

        {/* Approve / reject controls — only for pending */}
        {lead.review_status === "pending" && (
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 space-y-2">
            <input
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="If rejecting — optional reason (e.g. 'guest-post outreach', 'SEO spam')"
              className="w-full border border-[#E5E5E5] px-2 h-8 text-xs focus:outline-none focus:border-[#0055FF] bg-white"
              data-testid="reject-reason-input"
            />
          </div>
        )}

        {/* Approved / rejected status footer */}
        {lead.review_status !== "pending" && (
          <div className="bg-[#F5F5F5] border border-[#E5E5E5] px-3 py-2 text-xs text-[#525252]">
            <strong className="uppercase tracking-wider">{lead.review_status}</strong> by {lead.reviewed_by || "—"} on {lead.reviewed_at ? formatLocal(lead.reviewed_at) : "—"}
            {lead.rejection_reason && <span> · Reason: {lead.rejection_reason}</span>}
            {lead.promoted_lead_id && (
              <button onClick={() => onView(lead.promoted_lead_id)} className="ml-2 text-[#0055FF] hover:underline">
                View lead in CRM →
              </button>
            )}
          </div>
        )}

        {/* Technical metadata — collapsed by default. Useful for security
            audits but noise for daily reviewers. */}
        <button
          onClick={() => setShowTech(v => !v)}
          className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3] hover:text-[#525252] inline-flex items-center gap-1 mt-2"
          data-testid="toggle-tech-details"
        >
          {showTech ? <CaretDown size={11} /> : <CaretRight size={11} />}
          Technical details
        </button>
        {showTech && (
          <div className="text-[11px] text-[#A3A3A3] grid grid-cols-2 gap-x-6 gap-y-1 pt-1">
            {lead.lead_type && <div><span className="text-[#A3A3A3]">Lead type:</span> <span className="text-[#525252]">{lead.lead_type}</span></div>}
            {lead.source_ip && <div><span className="text-[#A3A3A3]">Source IP:</span> <span className="text-[#525252] font-mono">{lead.source_ip}</span></div>}
            {lead.user_agent && <div className="col-span-2 break-all"><span className="text-[#A3A3A3]">User agent:</span> <span className="text-[#525252] font-mono">{lead.user_agent}</span></div>}
            <div className="col-span-2 break-all"><span className="text-[#A3A3A3]">Raw reasons:</span> <span className="text-[#525252] font-mono">{lead.suspicion_reasons || "—"}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}
